import { useAuth } from '../../contexts/AuthContext';
import { Tag, Calendar, Briefcase, CreditCard, Sparkles, TrendingUp, Users } from 'lucide-react';

interface HomeProps {
  onNavigate: (page: string) => void;
}

export function Home({ onNavigate }: HomeProps) {
  const { profile } = useAuth();

  const quickActions = [
    {
      id: 'discounts',
      title: 'Browse Discounts',
      description: 'Discover exclusive deals from local businesses',
      icon: Tag,
      color: 'from-orange-500 to-red-500',
    },
    {
      id: 'events',
      title: 'Find Events',
      description: 'Join community gatherings and workshops',
      icon: Calendar,
      color: 'from-green-500 to-emerald-500',
    },
    {
      id: 'jobs',
      title: 'Explore Jobs',
      description: 'Find opportunities that match your skills',
      icon: Briefcase,
      color: 'from-indigo-500 to-blue-500',
    },
    {
      id: 'digital-id',
      title: 'PLIP ID',
      description: 'Generate your verified community ID card',
      icon: CreditCard,
      color: 'from-blue-500 to-cyan-500',
    },
  ];

  const stats = [
    { label: 'Active Discounts', value: '150+', icon: Tag },
    { label: 'Upcoming Events', value: '45+', icon: Calendar },
    { label: 'Job Openings', value: '80+', icon: Briefcase },
    { label: 'Community Members', value: '5K+', icon: Users },
  ];

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-12">
          <div className="glass-card bg-gradient-to-r from-indigo-600 to-blue-600 p-8 text-white shadow-xl glow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Sparkles className="w-6 h-6" />
                  <span className="text-sm font-semibold uppercase tracking-wide">Welcome back</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">
                  Hello, {profile?.full_name}!
                </h1>
                <p className="text-blue-100 text-lg">
                  {profile?.user_type === 'vendor'
                    ? 'Manage your listings and connect with the community'
                    : 'Discover amazing perks and opportunities in your community'}
                </p>
              </div>
              <div className="hidden md:block">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <TrendingUp className="w-10 h-10" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all">
                    <Icon className="w-6 h-6 mb-2" />
                    <div className="text-2xl font-bold">{stat.value}</div>
                    <div className="text-sm text-blue-100">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={() => onNavigate(action.id)}
                  className="glass-card p-6 text-left group"
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${action.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{action.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{action.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {profile?.user_type === 'vendor' && (
          <div className="glass-card p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Vendor Dashboard</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              As a vendor, you can post discounts, events, and job listings to reach the community.
            </p>
            <button
              onClick={() => onNavigate('vendor-post')}
              className="glass-button"
            >
              Create New Post
            </button>
          </div>
        )}

        <div className="glass-card p-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/20">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Get Verified</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Verify your account to unlock premium discounts and exclusive community features.
                Visit your profile to complete verification.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
